import { Button } from '@socialchorus/shared-ui-components';
import { useState, useEffect, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { Comment, CommentWithReplies } from '../../../models/comments/types';
import { CommentContext } from '../../comments/types';

import CommentListItem from './comment-list-item';
import { getFeatureFlag } from '../../../models/features/features';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { Feature } from '../../../models/features/features';

type CommentRepliesProps = {
  comment: Comment | CommentWithReplies;
  nestedLevel: number;
  context: CommentContext;
  onEdit?: (comment: Comment) => void;
  onDelete?: (comment: Comment) => void;
  onReply?: (comment: Comment, ref: RefObject<HTMLDivElement>) => void;
  onReport?: (comment: Comment) => void;
  onReplies?: (comment: Comment | CommentWithReplies) => Promise<void>;
};

const CommentReplies = ({
  comment,
  nestedLevel,
  context,
  onEdit,
  onDelete,
  onReport,
  onReplies,
  onReply,
}: CommentRepliesProps) => {
  const [isFetchingReplies, setIsFetchingReplies] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const { t } = useTranslation();

  const handleRepliesToggle = async () => {
    setIsVisible(!isVisible);
    setIsFetchingReplies(true);

    await onReplies?.(comment);

    setIsFetchingReplies(false);
  };

  const replies = 'replies' in comment ? comment.replies : [];
  const consensusMigration = usePatronSelector((state) =>
    getFeatureFlag(state, Feature.DISCOURSE_CONSENSUS_MIGRATION)
  );
  const replyPaginationEnabled =
    consensusMigration === 'ReadConsensusWriteBoth' ||
    consensusMigration === 'OnlyConsensus';
  const hasMoreReplies =
    'repliesPageCount' in comment &&
    'repliesCurrentPage' in comment &&
    comment.repliesPageCount > comment.repliesCurrentPage;

  const handleLoadMoreReplies = async () => {
    setIsFetchingReplies(true);
    await onReplies?.(comment);
    setIsFetchingReplies(false);
  };

  // enforcing only 40 comments shown until pagination supported
  const replyCount = comment.replyCount > 40 ? 40 : comment.replyCount;

  const replyButtonText = isVisible
    ? t('comments.hide_reply', { count: replyCount })
    : t('comments.show_reply', { count: replyCount });

  useEffect(() => {
    setIsVisible(!!replies.length);
  }, [replies.length]);

  return (
    <div className="comment__replies">
      <Button
        variant="text"
        label={replyButtonText}
        isLoading={isFetchingReplies}
        onClick={handleRepliesToggle}
        size="compact"
      />
      <div className={'comment__replies-body'}>
        {isVisible ? (
          <>
            {replies.map((reply) => (
              <CommentListItem
                comment={reply}
                context={context}
                nestedLevel={nestedLevel + 1}
                onEdit={onEdit}
                onDelete={onDelete}
                onReport={onReport}
                onReply={onReply}
                onReplies={onReplies}
                key={reply.id}
              />
            ))}
            {hasMoreReplies && replyPaginationEnabled && (
              <Button
                size="compact"
                variant="text"
                label={t('comments.show_more_replies')}
                onClick={handleLoadMoreReplies}
              />
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default CommentReplies;
